import React, { useState, useContext, useEffect, FC, useRef } from 'react';
import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import styles from './Header.module.scss';
import { AuthContext } from '../../context/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import Logo from '../../assets/img/blue-movie-logo.svg';
import { Menu } from './components/Menu';
import { DesktopSearchBar } from './components/DesktopSearchBar';
import { MobileSearchBar } from './components/MobileSearchBar';
import { CustomLink } from '../CustomLink';
import { listenForOutsideClick } from 'src/utils/listenForOutsideClicks';
import { loginRedirection } from '../../context/auth/utils';

const Header: FC = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const [searchBar, setSearchBar] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const authContext = useContext(AuthContext);
  const { isAuthenticated, logout } = authContext;
  const [navExpanded, setNavExpanded] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const location = useLocation();
  const menuRef = useRef(null);
  const searchBarRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [searchBarListening, setSearchBarListening] = useState(false);

  useEffect(listenForOutsideClick(listening, setListening, menuRef, setShowMenu));
  useEffect(listenForOutsideClick(searchBarListening, setSearchBarListening, searchBarRef, setSearchBar));

  useEffect(() => {
    setSearchBar(false);
    const urlSearchTerm = new URLSearchParams(location.search).get('q');
    setSearchTerm(urlSearchTerm || '');
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
  };

  const handleSearch = ({ key }: { key: string }) => {
    if (key === 'Enter') {
      navigate(`/${language}/search?q=${searchTerm}`);
      setSearchBar(false);
      closeNav();
    }
  };

  const submitSearch = () =>{
    navigate(`/${language}/search?q=${searchTerm}`);
    setSearchBar(false);
    closeNav();
  }

  const setNav = (expanded: boolean) => {
    setNavExpanded(expanded);
  };

  const closeNav = () => {
    setNavExpanded(false);
  };

  const toggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowMenu((prevState) => !prevState);
  };


  return (
    <div>
      <header className="header">
        <Container>
          <Navbar onToggle={setNav} expanded={navExpanded} className={styles.navbarWrapper} expand="md">
            <Navbar.Brand className="mr-auto">
              <div className={styles.logoWrapper}>
                <CustomLink to="/" variant="unstyled">
                  <img src={Logo} alt="Blue Movie" />
                </CustomLink>
                <div className={styles.logoCaption}>
                  <div className={styles.navbarTitle}>
                    <CustomLink to="/" variant="unstyled">{t('header.iam18')}</CustomLink>
                  </div>
                </div>
              </div>
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="basic-navbar-nav" className="ml-auto">
              <span className={styles.iconBar}></span>
              <span className={styles.iconBar}></span>
              <span className={styles.iconBar}></span>
            </Navbar.Toggle>
            <Navbar.Collapse className={`${styles.navbar} ml-auto`} id="basic-navbar-nav">
              {<MobileSearchBar handleSearch={handleSearch} setSearchTerm={setSearchTerm} submitSearch={submitSearch} searchTerm={searchTerm}/>}
              <Nav onClick={() => closeNav()} className={`${styles.mainNavigation} ml-auto`}>
                <div ref={searchBarRef}>
                  <span className={styles.searchIcon} onClick={() => setSearchBar(!searchBar)}>
                    <i className="fa fa-search" aria-hidden="true" />
                  </span>
                  {searchBar && <DesktopSearchBar handleSearch={handleSearch} setSearchTerm={setSearchTerm} submitSearch={submitSearch} searchTerm={searchTerm}/>}
                </div>
                <div className={styles.vl}></div>
                <div className={styles.linkWrapper}>
                  <CustomLink className={styles.navLink} to={`/${language}/movies`} variant="unstyled">
                    {t('menu.movies')}
                  </CustomLink>
                </div>
                {isAuthenticated && (
                  <>
                    <div className={styles.vl}></div>
                    <div className={styles.linkWrapper}>
                      <CustomLink className={styles.navLink} to={`/${language}/brandnew`} variant="unstyled">
                        {t('menu.brandNew')}
                      </CustomLink>
                    </div>
                  </>
                )}
                {isAuthenticated && (
                  <>
                    <div className={styles.vl}></div>
                    <div className={styles.linkWrapper}>
                      <CustomLink className={styles.navLink} to={`/${language}/blue-movie-plus`} variant="unstyled">
                        {t('menu.blueMoviePlus')}
                      </CustomLink>
                    </div>
                  </>
                )}
                {isAuthenticated && (
                  <>
                    <div className={styles.vl} />
                    <div className={styles.linkWrapper}>
                      <CustomLink className={styles.navLink} to={`/${language}/themes/16`} variant="unstyled">
                        {t('menu.exclusive')}
                      </CustomLink>
                    </div>
                  </>
                )}
                <div className={styles.vl} />
                <div className={styles.linkWrapper}>
                  <CustomLink className={styles.navLink} to={`/${language}/preise`} variant="unstyled">
                    {t('menu.price')}
                  </CustomLink>
                </div>
                {isAuthenticated && (
                  <p className={`${styles.linkWrapper} ${styles.signout}`} onClick={handleLogout}>
                    {t('settings.signout')}
                  </p>
                )}
                <div className={styles.vl}></div>
                {!isAuthenticated && (
                  <div className={styles.linkWrapper} onClick={loginRedirection}>
                    <span className={`${styles.navLink} ${styles.loginSettingsIcon}`}>
                      {t('menu.login')} <i className="fa fa-user" aria-hidden="true" />
                    </span>
                  </div>
                )}
                {isAuthenticated && (
                  <div ref={menuRef} className={styles.linkWrapper}>
                    <div onClick={(event) => toggle(event)}>
                      <span className={`${styles.navLink} ${styles.accountLogo}`}>
                        <span className={styles.accountText}>{t('menu.yourAccount')}</span>
                        <i className="fa fa-cog" aria-hidden="true"></i>
                      </span>
                    </div>
                  </div>
                )}
              </Nav>
            </Navbar.Collapse>
            {isAuthenticated && showMenu && (
              <Menu ref={menuRef} handleLogout={handleLogout} setShowMenu={setShowMenu} />
            )}
          </Navbar>
        </Container>
      </header>
    </div>
  );
};

export default Header;
